var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FormControl, Link, MenuItem, Select, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIsFinancialHidden } from '../../app/useIsFinancialHidden';
import { UnControlledFormField } from '../../common';
import { currencySelectMenuProps } from '../../hooks';
import { CurrencyConversionModal } from '../../orgConfigWizard/financials/CurrencyConversionModal';
import { QueuePanel } from '../common/queuePanel';
import { StepContainer, StepPanel } from '../common/stepContainer';
import { WizardContext } from '../context';
import { usePlanFxRatesAPI, useSavePlanFxRatesAPI } from '../selectors';
import { OvertimeCost } from './overtimeCost';
import { StaffSalaries } from './staffSalaries';
var StyledLink = styled(Link)(function (_a) {
    var theme = _a.theme;
    return ({
        color: theme.palette.primary.main,
        '&:hover': {
            textDecoration: 'underline',
        },
        '&:disabled': {
            color: theme.palette.grey[600],
            textDecorationColor: theme.palette.grey[600],
            opacity: 1,
        },
    });
});
export var PlanCurrencyConversion = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var _k = _a === void 0 ? {} : _a, propRatesData = _k.ratesData, propFetchRates = _k.fetchRates, propSaveData = _k.saveData, propSaveRates = _k.saveRates;
    var t = useTranslation('orgConfig', { keyPrefix: 'financials' }).t;
    var _l = useState(false), open = _l[0], setOpen = _l[1];
    var wizardContext = useContext(WizardContext);
    var handleSave = wizardContext === null || wizardContext === void 0 ? void 0 : wizardContext.handleSave;
    var formContext = useFormContext();
    var isDirty = (_c = (_b = formContext === null || formContext === void 0 ? void 0 : formContext.formState) === null || _b === void 0 ? void 0 : _b.isDirty) !== null && _c !== void 0 ? _c : false;
    var _m = usePlanFxRatesAPI(), hookRatesData = _m[0], hookFetchRates = _m[1];
    var _o = useSavePlanFxRatesAPI(), hookSaveData = _o[0], hookSaveRates = _o[1];
    var ratesData = propRatesData !== null && propRatesData !== void 0 ? propRatesData : hookRatesData;
    var fetchRates = propFetchRates !== null && propFetchRates !== void 0 ? propFetchRates : hookFetchRates;
    var saveData = propSaveData !== null && propSaveData !== void 0 ? propSaveData : hookSaveData;
    var saveRates = propSaveRates !== null && propSaveRates !== void 0 ? propSaveRates : hookSaveRates;
    var rates = useMemo(function () { var _a, _b; return (_b = (_a = ratesData.data) === null || _a === void 0 ? void 0 : _a.rates) !== null && _b !== void 0 ? _b : []; }, [(_d = ratesData.data) === null || _d === void 0 ? void 0 : _d.rates]);
    var rollupCurrency = (_f = (_e = ratesData.data) === null || _e === void 0 ? void 0 : _e.rollupCurrency) !== null && _f !== void 0 ? _f : 'USD';
    var hasRates = rates.length > 0;
    var conversionAvailable = hasRates || isDirty;
    useEffect(function () {
        fetchRates();
    }, [fetchRates]);
    var sourceRates = useMemo(function () { return rates.map(function (r) { return ({ currencyCode: r.currencyCode, providerRate: r.sourceRate }); }); }, [rates]);
    var handleRatesSave = function (rows) {
        saveRates({ rates: rows });
        fetchRates();
    };
    var handleOpen = function () {
        if (isDirty && handleSave) {
            return handleSave('submit', function () {
                fetchRates();
                setOpen(true);
            })();
        }
        fetchRates();
        setOpen(true);
    };
    return (_jsxs(Stack, __assign({ flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 3 }, { children: [_jsx(Typography, { children: t('rollup.label') }), _jsx(UnControlledFormField, __assign({ sx: { minWidth: 180 } }, { children: _jsx(FormControl, __assign({ fullWidth: true, size: "small" }, { children: _jsx(Select, __assign({ value: rollupCurrency, disabled: true, MenuProps: currencySelectMenuProps }, { children: _jsx(MenuItem, __assign({ value: rollupCurrency }, { children: rollupCurrency })) })) })) })), _jsx(StyledLink, __assign({ component: "button", type: "button", disabled: !conversionAvailable, onClick: conversionAvailable ? handleOpen : undefined }, { children: t('conversion.link') })), _jsx(CurrencyConversionModal, { open: open, rollupCurrency: rollupCurrency, rates: rates.map(function (r) { return ({
                    currencyCode: r.currencyCode,
                    corporateOverrideRate: r.corporateOverrideRate,
                    lastUpdated: null,
                }); }), providerRates: sourceRates, providerRatesLoading: ratesData.requesting || saveData.requesting, providerRatesError: Boolean(ratesData.error || saveData.error), fxSourceLabel: t('conversion.fxSource'), providerRatesLastUpdated: (_h = (_g = ratesData.data) === null || _g === void 0 ? void 0 : _g.providerTimestamp) !== null && _h !== void 0 ? _h : null, onClose: function () { return setOpen(false); }, onSave: handleRatesSave, readOnly: Boolean((_j = ratesData.data) === null || _j === void 0 ? void 0 : _j.isReadOnly) })] })));
};
export var FinancialStep = function () {
    var isFinancialHidden = useIsFinancialHidden();
    return (_jsx(StepContainer, __assign({ leftContent: _jsx(QueuePanel, {}) }, { children: _jsx(StepPanel, { children: _jsxs(Stack, __assign({ direction: "column", spacing: 8 }, { children: [!isFinancialHidden && _jsx(StaffSalaries, {}), _jsx(OvertimeCost, {})] })) }) })));
};
